/**
 * Signature Service
 *
 * Hỗ trợ các loại ký số:
 * - IMAGE: Ký hình ảnh (default)
 * - USB_TOKEN: USB Token (Viettel, VNPT, FPT)
 * - HSM: Hardware Security Module
 * - CA: Remote CA (requires OTP authentication)
 *
 * Provider code format: "PROVIDER-TYPE"
 * Examples: "VIETTEL-USB", "MYSIGN-CA", "ICORP-HSM"
 *
 * Ưu tiên đọc config:
 * 1. user-metadata.field5 (cấp user)
 * 2. system config USB_SIGNATURE_PROVIDER (cấp đơn vị)
 * 3. Nếu rỗng → IMAGE
 *
 * Usage:
 *   import { getSignatureConfig, SignatureType, SignatureService } from './signatureService';
 */

import { localStorageItem } from '../utils/LocalStorage';
import { KEY_LOCALSTORAGE } from '../auth/core/_consts';
import { APP_CONFIGURATION_KEYS } from '../utils/Constant';
import {
  SignatureType,
  SignatureProvider,
  SignatureConfig,
} from './signatureApiModels';
import viettelCAProvider from './viettelCAProvider';
import {
  SignatureFactoryRegistry,
  SignType,
  SignProvider,
  SigningResult,
  SigningOptions,
  XmlSigningOptions,
  BatchSigningItem,
  BatchSigningResult,
  ProviderConfig,
  ISignatureProvider,
} from './factories';

// ========================================
// Re-export types từ signatureApiModels
// ========================================
export { SignatureType, SignatureProvider } from './signatureApiModels';
export type { SignatureConfig } from './signatureApiModels';

/**
 * Detect signature provider from provider code string
 *
 * Format: "PROVIDER-TYPE"
 * Examples:
 * - "VIETTEL-USB" → VIETTEL_USB → USB_TOKEN
 * - "VIETTEL-HSM" → VIETTEL_HSM → HSM
 * - "MYSIGN-CA" → MYSIGN_CA → CA
 * - "" → IMAGE
 */
const detectSignatureProvider = (providerCode: string): SignatureProvider => {
  if (!providerCode) return SignatureProvider.IMAGE;

  const upperCode = providerCode.toUpperCase().trim();

  // ========================================
  // USB Providers
  // ========================================
  if (upperCode === 'VIETTEL-USB') return SignatureProvider.VIETTEL_USB;
  if (upperCode === 'VNPT-USB') return SignatureProvider.VNPT_USB;
  if (upperCode === 'FPT-USB') return SignatureProvider.FPT_USB;

  // ========================================
  // HSM Providers
  // ========================================
  if (upperCode === 'VIETTEL-HSM') return SignatureProvider.VIETTEL_HSM;
  if (upperCode === 'VNPT-HSM') return SignatureProvider.VNPT_HSM;
  if (upperCode === 'FPT-HSM') return SignatureProvider.FPT_HSM;

  // ========================================
  // CA Providers (Remote CA)
  // ========================================
  if (upperCode === 'VIETTEL-CA') return SignatureProvider.VIETTEL_CA;
  if (upperCode === 'VNPT-CA') return SignatureProvider.VNPT_CA;
  if (upperCode === 'FPT-CA') return SignatureProvider.FPT_CA;
  if (upperCode === 'MYSIGN-CA') return SignatureProvider.MYSIGN_CA;
  if (upperCode === 'ICORP-CA') return SignatureProvider.ICORP_CA;
  if (upperCode === 'EASY-CA') return SignatureProvider.EASY_CA;
  if (upperCode === 'BKAV-CA') return SignatureProvider.BKAV_CA;

  // ========================================
  // Fallback patterns
  // ========================================
  if (upperCode.includes('VIETTEL') && upperCode.includes('USB')) {
    return SignatureProvider.VIETTEL_USB;
  }
  if (upperCode.includes('VIETTEL') && upperCode.includes('HSM')) {
    return SignatureProvider.VIETTEL_HSM;
  }
  if (upperCode.includes('VIETTEL') && upperCode.includes('CA')) {
    return SignatureProvider.VIETTEL_CA;
  }
  if (upperCode.includes('MYSIGN')) {
    return SignatureProvider.MYSIGN_CA;
  }
  if (upperCode.includes('ICORP')) {
    return SignatureProvider.ICORP_CA;
  }

  // Default to IMAGE signature
  return SignatureProvider.IMAGE;
};

/**
 * Get signature type from provider
 */
const getSignatureType = (provider: SignatureProvider): SignatureType => {
  const providerStr = provider.toString();
  if (providerStr.includes('USB')) return SignatureType.USB_TOKEN;
  if (providerStr.includes('HSM')) return SignatureType.HSM;
  if (providerStr.includes('CA')) return SignatureType.CA;
  return SignatureType.IMAGE;
};

/**
 * Get signature configuration
 *
 * Ưu tiên đọc từ:
 * 1. user-metadata.field5 (cấp user)
 * 2. system config USB_SIGNATURE_PROVIDER (cấp đơn vị)
 * 3. Nếu cả hai rỗng → IMAGE (ký hình ảnh)
 *
 * Signature image luôn lấy từ user-metadata.field7
 */
export const getSignatureConfig = (): SignatureConfig => {
  // Lấy user metadata
  const userMetadata = localStorageItem.get(KEY_LOCALSTORAGE.USER_METADATA) || {};

  // Đọc từ field5 TRƯỚC (cấp user)
  let providerCode = userMetadata.field5 || SignatureProvider.MYSIGN_CA;

  // Fallback: Nếu field5 rỗng, đọc từ system config
  // if (!providerCode) {
  //   const systemConfig = localStorageItem.get(KEY_LOCALSTORAGE.CONFIGURATION) || {};
  //   providerCode = systemConfig[APP_CONFIGURATION_KEYS.USB_SIGNATURE_PROVIDER] || '';
  //   if (providerCode) {
  //     console.log(`getSignatureConfig: field5 empty, fallback to system config: "${providerCode}"`);
  //   }
  // } else {
  //   console.log(`getSignatureConfig: using field5: "${providerCode}"`);
  // }

  // Lấy image signature từ user metadata field7
  const field7 = userMetadata.field7 || '';

  // Detect provider và type
  const provider = detectSignatureProvider(providerCode);
  const type = getSignatureType(provider);

  console.log(`getSignatureConfig: provider=${provider}, type=${type}`);

  return {
    type,
    provider,
    imageSignature: field7,
  };
};

/**
 * Get USB provider instance based on SignatureProvider enum
 */
const getUsbProvider = (provider: SignatureProvider): any => {
  switch (provider) {
    case SignatureProvider.VIETTEL_USB:
      return viettelCAProvider;

    case SignatureProvider.VNPT_USB:
      throw new Error('VNPT USB provider not yet implemented');

    case SignatureProvider.FPT_USB:
      throw new Error('FPT USB provider not yet implemented');

    default:
      throw new Error(`Unsupported USB provider: ${provider}`);
  }
};

/**
 * Get current USB provider based on configuration
 */
export const getCurrentUsbProvider = (): any => {
  const config = getSignatureConfig();

  if (config.type !== SignatureType.USB_TOKEN) {
    throw new Error(`Current signature type is ${config.type}, not USB_TOKEN`);
  }

  return getUsbProvider(config.provider);
};

/**
 * Initialize USB plugin based on configuration
 */
export const initUsbPlugin = (): void => {
  try {
    const config = getSignatureConfig();

    if (config.type !== SignatureType.USB_TOKEN) {
      console.warn('Current signature type is not USB_TOKEN, skipping USB plugin init');
      return;
    }

    const provider = getCurrentUsbProvider();
    provider.initPlugin();
  } catch (error) {
    console.warn('USB plugin init warning:', error);
  }
};

/**
 * Sign BHYT XML using current USB provider
 * Hiện tại chỉ ViettelCA hỗ trợ method này
 */
export const signBHYTXml = async (xmlContent: string): Promise<string> => {
  const provider = getCurrentUsbProvider();

  if (typeof provider.signBHYTXml !== 'function') {
    throw new Error(`Current provider does not support signBHYTXml method`);
  }

  return provider.signBHYTXml(xmlContent);
};

// ========================================
// SignatureService class - Abstract Factory wrapper
// ========================================

/**
 * Convert SignatureType sang SignType (Abstract Factory)
 */
const convertToSignType = (type: SignatureType): SignType => {
  switch (type) {
    case SignatureType.USB_TOKEN:
      return SignType.USB;
    case SignatureType.HSM:
      return SignType.HSM;
    case SignatureType.CA:
      return SignType.CA;
    default:
      return SignType.IMAGE;
  }
};

/**
 * Convert SignatureProvider sang SignProvider (Abstract Factory)
 */
const convertToSignProvider = (provider: SignatureProvider): SignProvider => {
  const providerStr = provider.toString().toUpperCase();

  if (providerStr.includes('VIETTEL')) return SignProvider.VIETTEL;
  if (providerStr.includes('VNPT')) return SignProvider.VNPT;
  if (providerStr.includes('FPT')) return SignProvider.FPT;
  if (providerStr.includes('MYSIGN')) return SignProvider.MYSIGN;
  if (providerStr.includes('ICORP')) return SignProvider.ICORP;
  if (providerStr.includes('EASY')) return SignProvider.EASY;
  if (providerStr.includes('BKAV')) return SignProvider.BKAV;

  return SignProvider.OCEANTECH;
};

/**
 * SignatureService - High-level API sử dụng Abstract Factory
 */
export class SignatureService {
  private signer: ISignatureProvider;
  private signType: SignType;
  private signProvider: SignProvider;

  constructor(signType: SignType, signProvider: SignProvider, config?: Partial<ProviderConfig>) {
    this.signType = signType;
    this.signProvider = signProvider;
    this.signer = SignatureFactoryRegistry.getSigner(signType, signProvider, config);
  }

  /**
   * Factory method - Tạo service từ config
   * Sử dụng logic getSignatureConfig() để đọc field5/system config
   */
  static fromConfig(config?: Partial<ProviderConfig>): SignatureService {
    const sigConfig = getSignatureConfig();

    // Convert từ enum cũ sang enum Abstract Factory
    const signType = convertToSignType(sigConfig.type);
    const signProvider = convertToSignProvider(sigConfig.provider);

    // Merge config với signature image từ field7
    const finalConfig: Partial<ProviderConfig> = {
      ...config,
      options: {
        ...config?.options,
        signatureImage: sigConfig.imageSignature,
      },
    };

    console.log(`SignatureService.fromConfig: type=${signType}, provider=${signProvider}`);

    return new SignatureService(signType, signProvider, finalConfig);
  }

  /**
   * Khởi tạo signer
   */
  async initialize(): Promise<boolean> {
    return this.signer.initialize();
  }

  /**
   * Kiểm tra signer đã sẵn sàng chưa
   */
  isReady(): boolean {
    return this.signer.isReady();
  }

  /**
   * Lấy thông tin về signer hiện tại
   */
  getSignerInfo(): { type: SignType; provider: SignProvider; displayName: string } {
    return {
      type: this.signType,
      provider: this.signProvider,
      displayName: this.signer.displayName,
    };
  }

  /**
   * Ký PDF
   */
  async signPdf(pdfData: string, options?: SigningOptions): Promise<SigningResult> {
    if (!this.signer.isReady()) {
      await this.signer.initialize();
    }
    return this.signer.signPdf(pdfData, options);
  }

  /**
   * Ký file
   */
  async signFile(fileData: string, fileName: string, options?: SigningOptions): Promise<SigningResult> {
    if (!this.signer.isReady()) {
      await this.signer.initialize();
    }
    return this.signer.signFile(fileData, fileName, options);
  }

  /**
   * Ký XML (BHYT, etc.)
   */
  async signXml(xmlContent: string, options?: XmlSigningOptions): Promise<SigningResult> {
    if (!this.signer.isReady()) {
      await this.signer.initialize();
    }
    return this.signer.signXml(xmlContent, options);
  }

  /**
   * Ký batch
   */
  async signBatch(items: BatchSigningItem[], options?: SigningOptions): Promise<BatchSigningResult> {
    if (!this.signer.isReady()) {
      await this.signer.initialize();
    }
    return this.signer.signBatch(items, options);
  }

  /**
   * Lấy thông tin certificate
   */
  async getCertificateInfo() {
    return this.signer.getCertificateInfo();
  }

  /**
   * Dọn dẹp resources
   */
  dispose(): void {
    this.signer.dispose();
  }
}

// ========================================
// Default instance management
// ========================================
let defaultService: SignatureService | null = null;

export function getDefaultSignatureService(): SignatureService {
  if (!defaultService) {
    defaultService = SignatureService.fromConfig();
  }
  return defaultService;
}

export function resetDefaultSignatureService(): void {
  if (defaultService) {
    defaultService.dispose();
    defaultService = null;
  }
}
